import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Bike, Package, Utensils, MapPin, Phone, Camera, CheckCircle2, ArrowRight,
  Loader2, Wallet, LogOut, RefreshCw, Truck, Navigation, X, Check, Store, Volume2,
  KeyRound, CloudRain, Sun, Cloud, Snowflake,
} from "lucide-react";
import api from "../lib/api";
import { makeAlarmBlobUrl } from "../lib/alarm";
import KeepScreenOnToggle from "../components/KeepScreenOnToggle";
import StripeConnectCard from "../components/StripeConnectCard";
import DomainSwitcher from "../components/DomainSwitcher";

// Constantes du flow livreur Nold Food
const NEW_ORDER_STATUSES = ["driver_assigned"];   // notification "nouvelle course"
const READY_STATUSES = ["ready_for_pickup"];      // notification sonore "commande prête à récupérer"
const PICKED_UP = ["picked_up", "out_for_delivery"]; // colis en main, en route client
const ACCEPT_TIMEOUT_S = 30;                     // délai d'acceptation

// Ouvre Google Maps navigation en mode voyage direct.
const gmapsNav = (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;

// Formatte un ISO datetime en label court fr-BE — ex. "sam. 15 févr. à 19h30"
const fmtScheduled = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString("fr-BE", { weekday: "short", day: "numeric", month: "short" });
    const time = d.toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" }).replace(":", "h");
    return `${day} à ${time}`;
  } catch { return ""; }
};

export default function LivreurDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // ---------------- Alerte "Nouvelle course" (30s, sirène, accept/refuse) ----------------
  const [pendingAlert, setPendingAlert] = useState(null); // order pour le modal
  const [countdown, setCountdown] = useState(ACCEPT_TIMEOUT_S);
  const [pinModalOrder, setPinModalOrder] = useState(null); // ordre à valider par PIN
  const [weather, setWeather] = useState(null);
  // Météo — motivation quotidienne (Open-Meteo, sans clé). Bruxelles par défaut.
  useEffect(() => {
    const lat = 50.8503, lon = 4.3517;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.current_weather) setWeather(d.current_weather); })
      .catch(() => { /* ignore météo, feature bonus */ });
  }, []);
  const audioRef = useRef(null);
  const alarmUrlRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const seenReadyRef = useRef(new Set()); // commandes déjà signalées "prêtes à récupérer"
  const countdownRef = useRef(null);
  const [readyAlert, setReadyAlert] = useState(null); // { id, restaurant_name, resto_address }

  // Ferme la notif système correspondant à cette commande (accepted/refused/expired)
  // afin que le livreur ne voie pas rester une notif fantôme après action.
  const clearPushForOrder = (orderId) => {
    if (!orderId) return;
    try {
      navigator.serviceWorker?.controller?.postMessage({
        type: "close-notif",
        tag: `nold-course-${orderId}`,
      });
    } catch { /* ignore */ }
  };

  // Génère la sirène synthétisée une seule fois au montage (même pattern qu'OrderAlert resto)
  useEffect(() => {
    try {
      const url = makeAlarmBlobUrl("klaxon");
      alarmUrlRef.current = url;
      audioRef.current = new Audio(url);
      audioRef.current.preload = "auto";
    } catch { /* ignore */ }
    return () => {
      try { audioRef.current?.pause(); } catch { /* ignore */ }
      if (alarmUrlRef.current) { try { URL.revokeObjectURL(alarmUrlRef.current); } catch { /* ignore */ } }
    };
  }, []);

  // ---------------- GPS temps réel (watchPosition + envoi backend) ----------------
  const [driverPos, setDriverPos] = useState(null);
  const [gpsError, setGpsError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, availableRes] = await Promise.all([
        api.get("/food/driver/orders"),
        api.get("/food/driver/available-orders").catch(() => ({ data: { items: [] } })),
      ]);
      setData(data);
      setAvailable(availableRes.data?.items || []);
      // Détecte les nouvelles courses en clé composite ID+driver_assigned_at :
      // - si une commande est réassignée au même livreur, driver_assigned_at change → modal se rouvre
      // - si un livreur a refusé puis reçoit une AUTRE commande, l'ID est différent → modal s'ouvre
      const items = data?.active || [];
      const sig = (o) => `${o.id}:${o.assigned_at || ""}`;
      const nouvelle = items.find((o) => NEW_ORDER_STATUSES.includes(o.status) && !seenIdsRef.current.has(sig(o)));
      if (nouvelle && !pendingAlert) {
        openAlert(nouvelle);
      }
      // Détecte les commandes assignées à ce livreur qui viennent de passer "prête à récupérer".
      // Signature = id+resto_ready_at → si le resto la remet prête (rare) le modal peut ré-armer.
      const readySig = (o) => `${o.id}:ready:${o.resto_ready_at || ""}`;
      const nouvellePrete = items.find((o) => READY_STATUSES.includes(o.status) && !seenReadyRef.current.has(readySig(o)));
      if (nouvellePrete && !pendingAlert && !readyAlert) {
        openReadyAlert(nouvellePrete);
      }
      // Purge les signatures ready pour les commandes qui ne sont plus "prêtes" (picked_up etc.)
      const readySigsNow = new Set(items.filter((o) => READY_STATUSES.includes(o.status)).map(readySig));
      readySigsNow.forEach((s) => seenReadyRef.current.add(s));
      for (const s of Array.from(seenReadyRef.current)) {
        if (!readySigsNow.has(s)) seenReadyRef.current.delete(s);
      }
      // Marque les commandes actives courantes comme vues.
      const activeSigs = new Set(items.map(sig));
      activeSigs.forEach((s) => seenIdsRef.current.add(s));
      // Purge les signatures des commandes qui ne sont plus actives (accepted, delivered, canceled…)
      // → si la même commande revient plus tard (rare mais possible en debug/admin), le modal peut rouvrir.
      for (const s of Array.from(seenIdsRef.current)) {
        if (!activeSigs.has(s)) seenIdsRef.current.delete(s);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger vos courses.");
    } finally {
      setLoading(false);
    }
  }, [pendingAlert, readyAlert]);

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  // Deep-link : /livreur?order=xxx OU /livreur?ride=xxx (venant de la notif push)
  useEffect(() => {
    const refId = params.get("order") || params.get("ride");
    if (refId && data?.active) {
      const o = data.active.find((x) => x.id === refId);
      if (o && NEW_ORDER_STATUSES.includes(o.status)) openAlert(o);
      params.delete("order"); params.delete("ride"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [data]);

  // Écoute les messages postMessage du service worker :
  //  - `nold-push`   : nouvelle push reçue (sound=true) → re-poll + ouvre modal sirène
  //  - `nold-notif-click` : le livreur a cliqué la notif système → même effet
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => {
      const t = e.data?.type;
      if (t !== "nold-push" && t !== "nold-notif-click") return;
      const p = e.data?.payload || {};
      if (t === "nold-push" && !p.sound) return; // ignore les push non-urgentes
      // Purge la signature de l'orderId concerné pour permettre au polling de rouvrir le modal
      const refId = p.orderId || p.rideId;
      if (refId) {
        Array.from(seenIdsRef.current).forEach((s) => {
          if (s.startsWith(`${refId}:`)) seenIdsRef.current.delete(s);
        });
      }
      // Re-poll immédiat — load() détectera la nouvelle course et appellera openAlert()
      load();
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [load]);

  // Débloque l'audio au premier geste (autoplay policy). Si l'utilisateur n'a pas
  // encore interagi avec la page, `audioBlocked` reste true → on affiche un bouton
  // proéminent "Appuyer pour activer le son" pour qu'il débloque avant l'arrivée
  // d'une commande (sinon le modal serait silencieux malgré la sirène).
  const [audioBlocked, setAudioBlocked] = useState(true);
  const unlockAudio = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    const v = a.volume; a.volume = 0;
    a.play().then(() => {
      a.pause(); a.currentTime = 0; a.volume = v;
      setAudioBlocked(false);
    }).catch(() => { setAudioBlocked(true); });
  }, []);
  useEffect(() => {
    const onUnlock = () => unlockAudio();
    window.addEventListener("pointerdown", onUnlock, { once: true });
    window.addEventListener("keydown", onUnlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onUnlock);
      window.removeEventListener("keydown", onUnlock);
    };
  }, [unlockAudio]);

  // Live GPS — envoi continu au backend tant que le livreur a le dashboard ouvert
  // (foreground : watchPosition haute précision + envoi paresseux)
  useEffect(() => {
    if (!navigator.geolocation) { setGpsError("Géolocalisation non supportée"); return; }
    const active = data?.active || [];
    const onTrip = active.length > 0;
    let cancelled = false;
    let lastPost = 0;
    const postInterval = onTrip ? 3000 : 15000; // 3s pendant une course, 15s à l'attente
    const handle = async (pos) => {
      if (cancelled) return;
      const c = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
      if (c.accuracy != null && c.accuracy > 20000) return; // IP fix pur → skip
      setDriverPos({ lat: c.lat, lng: c.lng });
      setGpsError(null);
      const now = Date.now();
      if (now - lastPost >= postInterval) {
        lastPost = now;
        try { await api.post("/driver/location", { lat: c.lat, lng: c.lng, accuracy: c.accuracy, status: onTrip ? "en_course" : "en_ligne" }); } catch { /* ignore */ }
      }
    };
    const onErr = (e) => {
      if (cancelled) return;
      setGpsError(e?.code === 1 ? "Localisation refusée — activez-la pour recevoir des courses" : (e?.message || "GPS indisponible"));
    };
    const watchId = navigator.geolocation.watchPosition(handle, onErr, { enableHighAccuracy: true, timeout: 20000, maximumAge: 2000 });
    return () => { cancelled = true; try { navigator.geolocation.clearWatch(watchId); } catch { /* ignore */ } };
  }, [data?.active?.length]);

  // ---------------- GPS BACKGROUND (persiste même onglet caché) ----------------
  // Envoi de position à la connexion + toutes les 2 minutes, indépendamment de
  // watchPosition qui peut être suspendu par le navigateur quand l'onglet est
  // en arrière-plan sur mobile. Sans ça, un livreur qui laisse l'app ouverte mais
  // change d'onglet devient "offline" pour l'auto-assign après 5-15 min.
  useEffect(() => {
    if (!navigator.geolocation) return;
    let cancelled = false;
    const pingLocation = () => {
      if (cancelled) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const { latitude: lat, longitude: lng, accuracy } = pos.coords;
          if (accuracy != null && accuracy > 20000) return;
          api.post("/driver/location", { lat, lng, accuracy, status: "en_ligne" }).catch(() => { /* ignore */ });
        },
        () => { /* silent — foreground watch gère les erreurs UX */ },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }, // maximumAge=60s pour économiser la batterie
      );
    };
    // Envoi immédiat à la connexion.
    pingLocation();
    // Puis toutes les 2 minutes.
    const iv = setInterval(pingLocation, 2 * 60 * 1000);
    // Bonus : rafraîchir dès que l'onglet reprend le focus (revient d'arrière-plan).
    const onVis = () => { if (document.visibilityState === "visible") pingLocation(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // ---------------- Modal "Commande prête à récupérer" (sonore, non intrusif — dismiss simple) ----------------
  const openReadyAlert = useCallback((order) => {
    setReadyAlert(order);
    seenReadyRef.current.add(`${order.id}:ready:${order.resto_ready_at || ""}`);
    if ("vibrate" in navigator) navigator.vibrate([300, 150, 300, 150, 300]);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.loop = true;
      audioRef.current.play().then(() => setAudioBlocked(false)).catch(() => setAudioBlocked(true));
    }
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  }, []);
  const dismissReadyAlert = () => {
    try { audioRef.current?.pause(); if (audioRef.current) audioRef.current.currentTime = 0; } catch { /* ignore */ }
    setReadyAlert(null);
  };

  // ---------------- Modal Accept/Refuse ----------------
  const openAlert = (order) => {
    setPendingAlert(order);
    setCountdown(ACCEPT_TIMEOUT_S);
    // Marque cette signature (id + driver_assigned_at) comme vue pour éviter la ré-ouverture
    // dans le même cycle de polling. Une VRAIE réassignation aura un nouvel `assigned_at`.
    seenIdsRef.current.add(`${order.id}:${order.assigned_at || ""}`);
    if ("vibrate" in navigator) navigator.vibrate([400, 200, 400, 200, 400]);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.loop = true;
      audioRef.current.play().then(() => setAudioBlocked(false)).catch(() => setAudioBlocked(true));
    }
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    // Timer 30s
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          // Auto-refuse RÉEL : appelle l'endpoint refuse-by-driver → désassigne + relance auto-assign
          const orderId = pendingAlert?.id;
          if (orderId) {
            api.post(`/food/order/${orderId}/refuse-by-driver`).catch(() => {});
            clearPushForOrder(orderId);
          }
          if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current.loop = false; }
          setPendingAlert(null);
          setCountdown(ACCEPT_TIMEOUT_S);
          toast.info("Course expirée — réattribution à un autre livreur");
          load();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const closeAlert = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current.loop = false; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    setPendingAlert(null);
    setCountdown(ACCEPT_TIMEOUT_S);
  };

  const acceptOrder = async () => {
    if (!pendingAlert) return;
    setBusy(pendingAlert.id);
    const orderId = pendingAlert.id;
    const pickupLat = pendingAlert.pickup_lat;
    const pickupLng = pendingAlert.pickup_lng;
    closeAlert();
    clearPushForOrder(orderId);
    try {
      // Étape 1 : accepter la course → statut arrived_at_restaurant (le livreur va au resto)
      await api.post(`/food/order/${orderId}/status`, { status: "arrived_at_restaurant" }).catch(() => {});
      toast.success("Course acceptée — direction le restaurant 🛵");
      // Lance Google Maps vers le resto AUTOMATIQUEMENT (nouvel onglet)
      if (pickupLat != null && pickupLng != null) {
        window.open(gmapsNav(pickupLat, pickupLng), "_blank");
      }
      await load();
      // Scroll la carte active en vue pour que le livreur voie le prochain bouton
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="active-order-${orderId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setBusy(null); }
  };

  const refuseOrder = async () => {
    if (!pendingAlert) return;
    setBusy(pendingAlert.id);
    const orderId = pendingAlert.id;
    closeAlert();
    clearPushForOrder(orderId);
    try {
      // Refuse : on désassigne côté backend (via l'endpoint admin retry) et on re-alloue.
      await api.post(`/food/order/${pendingAlert.id}/refuse-by-driver`).catch(() => {});
      toast.info("Course refusée — on cherche un autre livreur");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setBusy(null); }
  };

  // ---------------- Actions courses en cours ----------------
  const arrivedAtRestaurant = async (orderId) => {
    setBusy(orderId);
    try {
      await api.post(`/food/order/${orderId}/status`, { status: "arrived_at_restaurant" });
      toast.success("Signalé au restaurant. Récupérez la commande 📦");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(null); }
  };

  const pickup = async (orderId) => {
    setBusy(orderId);
    // Récupère l'ordre AVANT le refresh (le status changera après)
    const o = data.active.find((x) => x.id === orderId);
    const dropLat = o?.dropoff_lat;
    const dropLng = o?.dropoff_lng;
    try {
      await api.post(`/food/order/${orderId}/status`, { status: "picked_up" });
      toast.success("Commande récupérée — direction le client 🛵");
      // Étape 2 : ouvre AUTOMATIQUEMENT Google Maps vers l'adresse du client
      if (dropLat != null && dropLng != null) {
        window.open(gmapsNav(dropLat, dropLng), "_blank");
      }
      await load();
      // Scroll la carte active en vue pour montrer le bouton "Confirmer la livraison"
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="active-order-${orderId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(null); }
  };

  const delivered = async (orderId, proofDataUrl = null) => {
    const o = data.active.find((x) => x.id === orderId);
    // PIN de signature — commandes haute valeur (≥ 80 €) : ouvre le keypad plein écran
    // au lieu d'un window.prompt (mieux UX + gros doigts sur mobile).
    if (o?.signature_required && !proofDataUrl) {
      setPinModalOrder(o);
      return;
    }
    await postDelivered(orderId, proofDataUrl, null);
  };

  const postDelivered = async (orderId, proofDataUrl, pinCode) => {
    setBusy(orderId);
    try {
      const body = { status: "delivered" };
      if (proofDataUrl) body.proof_photo_data_url = proofDataUrl;
      if (pinCode) body.signature_pin = pinCode;
      await api.post(`/food/order/${orderId}/status`, body);
      toast.success("Livraison confirmée ✅");
      setPinModalOrder(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(null); }
  };

  // Accepter manuellement une commande disponible (Feature 2)
  const acceptAvailable = async (orderId) => {
    setBusy(orderId);
    try {
      const { data: r } = await api.post(`/food/order/${orderId}/accept-available`);
      toast.success("Course prise ! Navigation vers le restaurant 🛵");
      // Ouvre Google Maps si on a les coordonnées pickup
      const o = available.find((x) => x.id === orderId);
      if (o?.pickup_lat != null) window.open(gmapsNav(o.pickup_lat, o.pickup_lng), "_blank");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cette course n'est plus disponible.");
      await load();
    } finally { setBusy(null); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0]"><Loader2 className="w-8 h-8 animate-spin text-[#059669]" /></div>;
  }

  const d = data || { active: [], delivered_today: [], earnings_today: {} };
  const earn = d.earnings_today || {};

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="livreur-dashboard">
      <DomainSwitcher />
      {/* Modal alerte nouvelle course Nold Food */}
      {pendingAlert && (
        <NewOrderAlertModal
          order={pendingAlert} countdown={countdown}
          onAccept={acceptOrder} onRefuse={refuseOrder}
          driverPos={driverPos}
        />
      )}
      {/* Modal "Commande prête" — apparaît quand le resto marque prête (push sonore) */}
      {readyAlert && (
        <div data-testid="ready-alert-modal" className="fixed inset-0 z-[3500] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 text-center shadow-2xl">
            <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-[#F26419] to-[#D94F2B] text-white flex items-center justify-center text-3xl animate-bounce">🍽️</div>
            <h3 className="mt-4 font-head font-black text-2xl text-[#1A1A1A]">Commande prête !</h3>
            <p className="mt-2 text-sm text-[#3B423E]">
              <b>{readyAlert.restaurant_name || "Le restaurant"}</b> a fini la préparation.<br/>
              Va récupérer la commande <span className="font-mono font-bold">#{readyAlert.id.slice(0, 8).toUpperCase()}</span>.
            </p>
            {readyAlert.resto_address && (
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F0EBE1] text-xs text-[#3B423E]">
                <MapPin className="w-3.5 h-3.5" /> {readyAlert.resto_address}
              </div>
            )}
            <button data-testid="ready-alert-dismiss" onClick={dismissReadyAlert}
              className="mt-5 w-full h-12 rounded-xl bg-[#F26419] hover:bg-[#D94F2B] text-white font-black text-sm">
              👍 J'ai vu — j'y vais
            </button>
          </div>
        </div>
      )}
      {/* Modal keypad PIN — livraison haute valeur */}
      {pinModalOrder && (
        <PinKeypadModal
          order={pinModalOrder}
          busy={busy === pinModalOrder.id}
          onSubmit={(pin) => postDelivered(pinModalOrder.id, null, pin)}
          onClose={() => setPinModalOrder(null)}
        />
      )}

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Hero header — équipe livreurs Nold Food (2 photos illustrant la diversité) */}
        <div className="relative rounded-3xl overflow-hidden mb-6 shadow-lg bg-gradient-to-br from-[#059669] to-[#047857]" data-testid="livreur-hero" style={{ minHeight: 160 }}>
          <div className="grid grid-cols-2 gap-0.5 bg-white/10">
            <img
              src="https://images.unsplash.com/photo-1690625642622-ba0bed1a68a0?fm=jpg&q=75&w=800&fit=crop"
              alt="Livreur Nold Food à vélo (Rotterdam)"
              className="w-full h-40 sm:h-56 object-cover bg-[#047857]"
              loading="eager"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <img
              src="https://images.unsplash.com/photo-1779583350228-566db01e9049?fm=jpg&q=75&w=800&fit=crop"
              alt="Livreuse Nold Food à e-bike"
              className="w-full h-40 sm:h-56 object-cover bg-[#047857]"
              loading="eager"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/25 backdrop-blur px-3 py-1 text-[10px] font-bold text-white uppercase tracking-wider drop-shadow">
              <Bike className="w-3 h-3" /> Nold Food · Livraison Bruxelles
            </div>
            <h1 className="mt-2 font-head font-black text-2xl sm:text-3xl text-white leading-tight drop-shadow-lg">Bonjour livreur ✨</h1>
            <p className="text-sm text-white mt-1 drop-shadow font-medium">Vos livraisons du jour, tarifs et navigation — tout est ici.</p>
          </div>
        </div>

        {/* Header actions */}
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">Vos livraisons</div>
            <GpsIndicator pos={driverPos} error={gpsError} />
          </div>
          <div className="flex items-center gap-2">
            <KeepScreenOnToggle color="green" compact />
            <button onClick={load} data-testid="livreur-refresh" className="w-10 h-10 rounded-full bg-white border border-[#E2DFD8] grid place-items-center hover:border-[#059669]" title="Rafraîchir">
              <RefreshCw className="w-4 h-4 text-[#75807A]" />
            </button>
            <button onClick={() => navigate("/")} data-testid="livreur-back" className="text-[#75807A] hover:text-[#1A1A1A] text-sm inline-flex items-center gap-1">
              <LogOut className="w-4 h-4" /> Retour
            </button>
          </div>
        </div>

        {/* Nouveau bloc stats accueil — gains jour/semaine, livraisons, note, statut en ligne */}
        <DriverHomeStats />

        {/* Météo — encouragement selon la condition (Open-Meteo, gratuit, sans clé) */}
        {weather && <WeatherBanner weather={weather} />}

        {/* Bouton activation son — visible tant que l'audio est bloqué (autoplay policy).
            À afficher AVANT l'arrivée d'une commande sinon le modal serait silencieux. */}
        {audioBlocked && (
          <button
            onClick={unlockAudio}
            data-testid="livreur-unlock-sound"
            className="w-full mb-4 inline-flex items-center justify-center gap-2 h-14 rounded-2xl bg-[#F26419] hover:bg-[#D94F2B] text-white font-bold text-base shadow-lg animate-pulse"
          >
            <Volume2 className="w-5 h-5" /> Appuyer pour activer le son des alertes
          </button>
        )}

        {/* Revenu du jour — Nold Food vert */}
        <div className="rounded-3xl bg-gradient-to-br from-[#059669] to-[#047857] text-white p-5" data-testid="livreur-earnings">
          <div className="text-[11px] font-bold uppercase tracking-wider opacity-90 inline-flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Aujourd'hui
          </div>
          <div className="mt-2 flex items-end gap-2">
            <div className="font-head font-black text-4xl leading-none" data-testid="earnings-total">
              {(earn.total_eur || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            <div className="text-sm opacity-90 pb-1">{earn.deliveries || 0} livraison{(earn.deliveries || 0) > 1 ? "s" : ""}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/15 backdrop-blur p-3">
              <div className="text-[10px] uppercase font-bold opacity-80">Frais livraison</div>
              <div className="font-head font-bold text-lg mt-0.5">{(earn.delivery_fees_eur || 0).toFixed(2)} €</div>
            </div>
            <div className="rounded-2xl bg-white/15 backdrop-blur p-3">
              <div className="text-[10px] uppercase font-bold opacity-80">Pourboires 💝</div>
              <div className="font-head font-bold text-lg mt-0.5">{(earn.tips_eur || 0).toFixed(2)} €</div>
            </div>
          </div>
        </div>

        {/* Commandes disponibles — Feature 2 : le livreur peut accepter manuellement */}
        {available.length > 0 && d.active.length === 0 && (
          <section className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-head font-black text-xl text-[#1A1A1A]">
                  Commandes disponibles
                  <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-amber-500 text-white text-xs font-bold animate-pulse">{available.length}</span>
                </h2>
                <p className="text-xs text-[#75807A]">Prenez-en une manuellement — premier arrivé, premier servi</p>
              </div>
            </div>
            <div className="space-y-2">
              {available.map((o) => (
                <div key={o.id} className="rounded-2xl bg-white border-2 border-amber-300 hover:border-amber-500 transition-colors p-4" data-testid={`available-order-${o.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 shrink-0 rounded-full bg-amber-100 grid place-items-center">
                      <Store className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-head font-bold text-[#1A1A1A] truncate">{o.restaurant_name}</div>
                      {o.scheduled && o.scheduled_for && (
                        <div
                          data-testid={`scheduled-badge-available-${o.id}`}
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#FFF3E6] border border-[#F26419] text-[#7C2D12] px-2 py-0.5 text-[11px] font-bold"
                        >
                          📅 Planifiée · {fmtScheduled(o.scheduled_for)}
                        </div>
                      )}
                      <div className="text-xs text-[#75807A] mt-1 flex items-start gap-1">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span className="truncate">{o.delivery_address}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                        {o.distance_km != null && (
                          <span className="inline-flex items-center gap-1 font-semibold text-[#1A1A1A]">
                            <Navigation className="w-3 h-3 text-[#059669]" /> {o.distance_km} km
                          </span>
                        )}
                        <span className="text-[#75807A]">{o.items_count} article{o.items_count > 1 ? "s" : ""}</span>
                        <span className="font-bold text-[#059669]">Gain ≈ {o.estimated_earning_eur.toFixed(2)} €</span>
                        {o.waiting_minutes > 3 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 font-bold">
                            ⏱ {o.waiting_minutes} min d'attente
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => acceptAvailable(o.id)}
                    disabled={busy === o.id}
                    data-testid={`accept-available-${o.id}`}
                    className="mt-3 w-full h-11 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {busy === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Prendre cette course
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Commandes actives */}
        <section className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head font-black text-xl text-[#1A1A1A]">
              Courses en cours
              {d.active.length > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-[#059669] text-white text-xs font-bold">{d.active.length}</span>}
            </h2>
          </div>
          {d.active.length === 0 ? (
            <div className="rounded-3xl bg-white border border-[#E2DFD8] p-6 text-center" data-testid="livreur-no-active">
              <Truck className="w-10 h-10 text-[#75807A] mx-auto opacity-50" />
              <p className="mt-2 text-sm text-[#75807A]">Aucune livraison en cours. Restez en ligne près des restaurants pour être assigné automatiquement.</p>
              {!driverPos && <p className="mt-2 text-xs text-red-600">⚠️ GPS inactif — vous ne recevrez aucune course tant que la localisation n'est pas activée.</p>}
            </div>
          ) : (
            <div className="space-y-3">
              {d.active.map((o) => (
                <ActiveOrderCard
                  key={o.id} order={o} busy={busy === o.id}
                  onArrivedAtResto={() => arrivedAtRestaurant(o.id)}
                  onPickup={() => pickup(o.id)}
                  onDelivered={(photo) => delivered(o.id, photo)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Livrées du jour */}
        {d.delivered_today.length > 0 && (
          <section className="mt-8">
            <h2 className="font-head font-black text-xl text-[#1A1A1A] mb-3">Livrées aujourd'hui</h2>
            <div className="space-y-2">
              {d.delivered_today.map((o) => (
                <div key={o.id} className="rounded-2xl bg-white border border-[#E2DFD8] p-3 flex items-center gap-3" data-testid={`delivered-${o.id}`}>
                  <div className="w-9 h-9 rounded-full bg-emerald-100 grid place-items-center shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#1A1A1A] text-sm truncate">{o.restaurant_name}</div>
                    <div className="text-xs text-[#75807A] truncate">{o.delivery_address}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-[#059669]">
                      {(o.delivery_fee_eur + o.tip_eur).toFixed(2)} €
                    </div>
                    {o.tip_eur > 0 && <div className="text-[10px] text-emerald-600 font-semibold">+{o.tip_eur.toFixed(2)}€ 💝</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Stripe Connect — reversement automatique 75% + pourboires 100% pour chaque livraison */}
        <section className="mt-8">
          <StripeConnectCard />
        </section>

        <div className="mt-8 text-center text-xs text-[#75807A]">Actualisé auto toutes les 15 secondes · Nold Food</div>
      </div>
    </div>
  );
}


// ============================================================================
// Modal — Nouvelle course (accept/refuse + timer 30s + sirène)
// ============================================================================
function NewOrderAlertModal({ order, countdown, onAccept, onRefuse, driverPos }) {
  const distToResto = (() => {
    if (!driverPos || order.pickup_lat == null) return null;
    const R = 6371;
    const dLat = (order.pickup_lat - driverPos.lat) * Math.PI / 180;
    const dLng = (order.pickup_lng - driverPos.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(driverPos.lat*Math.PI/180) * Math.cos(order.pickup_lat*Math.PI/180) * Math.sin(dLng/2)**2;
    return (R * 2 * Math.asin(Math.sqrt(a))).toFixed(1);
  })();
  const pct = (countdown / ACCEPT_TIMEOUT_S) * 100;
  const urgent = countdown <= 10;
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 animate-in fade-in" data-testid="livreur-alert-modal">
      <div className={`w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden ${urgent ? "ring-4 ring-red-500 animate-pulse" : "ring-4 ring-[#059669]"}`}>
        {/* Header vert Nold Food */}
        <div className={`px-6 py-4 text-white ${urgent ? "bg-red-600" : "bg-[#059669]"}`}>
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <Bike className="w-5 h-5" />
              <div className="font-black uppercase text-sm tracking-wider">Nouvelle course</div>
            </div>
            <div className="font-black text-3xl tabular-nums" data-testid="alert-countdown">{countdown}s</div>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/30 overflow-hidden">
            <div className="h-full bg-white transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-[#059669]/10 grid place-items-center shrink-0">
              <Store className="w-5 h-5 text-[#059669]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-head font-bold text-lg text-[#1A1A1A] truncate">{order.restaurant_name || "Restaurant"}</div>
              <div className="text-xs text-[#75807A] mt-0.5">Récupération</div>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-3 pl-14">
            <ArrowRight className="w-4 h-4 text-[#75807A] mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#1A1A1A] truncate">{order.delivery_address}</div>
              <div className="text-xs text-[#75807A] mt-0.5">Livraison</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-[#F0FDF4] p-3">
              <div className="text-[10px] font-bold uppercase text-[#047857]">Distance</div>
              <div className="font-bold text-[#1A1A1A] mt-0.5">{distToResto ? `${distToResto} km` : "—"}</div>
            </div>
            <div className="rounded-xl bg-[#F0FDF4] p-3">
              <div className="text-[10px] font-bold uppercase text-[#047857]">Articles</div>
              <div className="font-bold text-[#1A1A1A] mt-0.5">{order.items_count || 1}</div>
            </div>
            <div className="rounded-xl bg-[#F0FDF4] p-3">
              <div className="text-[10px] font-bold uppercase text-[#047857]">Gain estimé</div>
              <div className="font-bold text-[#059669] mt-0.5">{((order.delivery_fee_eur || 0) * 0.75).toFixed(2)}€</div>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              onClick={onRefuse}
              data-testid="alert-refuse"
              className="h-14 rounded-xl bg-white border-2 border-red-300 hover:bg-red-50 text-red-600 font-bold text-base inline-flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" /> Refuser
            </button>
            <button
              onClick={onAccept}
              data-testid="alert-accept"
              className="h-14 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-black text-base inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/40"
            >
              <Check className="w-5 h-5" /> Accepter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Carte d'une course active
// ============================================================================
function ActiveOrderCard({ order, busy, onArrivedAtResto, onPickup, onDelivered }) {
  const [showPhotoInput, setShowPhotoInput] = useState(false);
  const [photoData, setPhotoData] = useState(null);
  const status = order.status || "driver_assigned";
  // Étape 1 : "Vers le restaurant" — regroupe driver_assigned + arrived_at_restaurant
  //   (l'acceptation depuis le modal set direct arrived_at_restaurant ; les 2 statuts
  //   partagent la même UI "récupérer au resto")
  const isOnRouteToResto = status === "driver_assigned" || status === "arrived_at_restaurant";
  const isAtResto = false; // fusion : un seul bouton "Commande récupérée" en étape 1
  const isOnRouteToCustomer = PICKED_UP.includes(status);

  const handlePhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo trop lourde (max 8 Mo)."); return; }
    const r = new FileReader();
    r.onload = () => setPhotoData(String(r.result || ""));
    r.readAsDataURL(f);
  };

  // Étape actuelle → couleur + label
  const stepColor = isOnRouteToCustomer ? "bg-[#059669]" : "bg-blue-600";
  const stepLabel = isOnRouteToCustomer ? "🛵 Étape 2 · Vers le client" : "🏪 Étape 1 · Récupérer au restaurant";

  return (
    <div className={`rounded-3xl bg-white border-2 p-5 shadow-md ${isOnRouteToCustomer ? "border-[#059669]" : isAtResto ? "border-amber-500" : "border-blue-500"}`} data-testid={`active-order-${order.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 shrink-0 rounded-full grid place-items-center ${isOnRouteToCustomer ? "bg-[#059669]/15" : isAtResto ? "bg-amber-100" : "bg-blue-100"}`}>
          {isOnRouteToCustomer ? <Bike className="w-5 h-5 text-[#059669]" /> : isAtResto ? <Store className="w-5 h-5 text-amber-600" /> : <Utensils className="w-5 h-5 text-blue-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-head font-bold text-[#1A1A1A] truncate">{order.restaurant_name}</div>
            <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white ${stepColor}`}>{stepLabel}</span>
            {order.scheduled && order.scheduled_for && (
              <span
                data-testid={`scheduled-badge-active-${order.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E6] border border-[#F26419] text-[#7C2D12] px-2 py-0.5 text-[10px] font-bold"
              >
                📅 {fmtScheduled(order.scheduled_for)}
              </span>
            )}
          </div>
          <div className="text-xs text-[#75807A] mt-1 flex items-start gap-1">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="truncate">{isOnRouteToCustomer || isAtResto ? order.delivery_address : "Restaurant"}</span>
          </div>
          <div className="text-xs text-[#75807A] mt-1">
            {order.items_count} article{order.items_count > 1 ? "s" : ""} · <b className="text-[#1A1A1A]">{(order.total_eur || 0).toFixed(2)} €</b>
          </div>
        </div>
      </div>

      {/* Actions rapides : appel client (le bouton nav complet est plus bas) */}
      {order.customer_phone && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <a href={`tel:${order.customer_phone}`} data-testid={`call-customer-${order.id}`}
             className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#F0EBE1] hover:bg-[#059669] hover:text-white text-[#1A1A1A] text-xs font-bold transition-colors">
            <Phone className="w-3.5 h-3.5" /> {order.customer_name || "Client"}
          </a>
        </div>
      )}

      {/* Bouton nav GPS proéminent (rappel manuel) — utile si le popup Google Maps
          a été bloqué à l'ouverture automatique. */}
      {isOnRouteToResto && order.pickup_lat != null && (
        <a href={gmapsNav(order.pickup_lat, order.pickup_lng)} target="_blank" rel="noreferrer"
           data-testid={`nav-resto-full-${order.id}`}
           className="mt-4 w-full h-14 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-md">
          <Navigation className="w-5 h-5" /> Naviguer vers le restaurant
        </a>
      )}
      {isOnRouteToCustomer && order.dropoff_lat != null && (
        <a href={gmapsNav(order.dropoff_lat, order.dropoff_lng)} target="_blank" rel="noreferrer"
           data-testid={`nav-customer-full-${order.id}`}
           className="mt-4 w-full h-14 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-md">
          <Navigation className="w-5 h-5" /> Naviguer vers le client
        </a>
      )}

      {/* Bouton d'action principal selon l'étape */}
      <div className="mt-3 flex gap-2">
        {isOnRouteToResto && (
          <button
            onClick={onPickup} disabled={busy}
            data-testid={`btn-pickup-${order.id}`}
            className="flex-1 h-14 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-base inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Package className="w-5 h-5" />}
            Commande récupérée
          </button>
        )}
        {isOnRouteToCustomer && !showPhotoInput && (
          <button
            onClick={() => setShowPhotoInput(true)}
            data-testid={`btn-show-photo-${order.id}`}
            className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm inline-flex items-center justify-center gap-2"
          >
            <Camera className="w-4 h-4" /> Confirmer la livraison
          </button>
        )}
        {isOnRouteToCustomer && showPhotoInput && (
          <div className="w-full space-y-2">
            <label className="block cursor-pointer" data-testid={`photo-drop-${order.id}`}>
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoChange} data-testid={`photo-input-${order.id}`} />
              <div className={`rounded-xl border-2 border-dashed p-3 text-center text-sm ${photoData ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-[#059669] bg-[#059669]/5 text-[#059669]"}`}>
                {photoData ? "✓ Photo prête — vous pouvez confirmer" : "📸 Photo du colis déposé (recommandée)"}
              </div>
              {photoData && <img src={photoData} alt="Preuve" className="mt-2 max-h-32 rounded-lg mx-auto object-contain" />}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowPhotoInput(false); setPhotoData(null); }}
                className="h-11 px-4 rounded-xl bg-white border border-[#E2DFD8] text-[#75807A] font-bold text-sm">
                Annuler
              </button>
              <button
                onClick={() => onDelivered(photoData)} disabled={busy}
                data-testid={`btn-delivered-${order.id}`}
                className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Livrée {!photoData && "(sans photo)"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// Indicateur GPS en direct
// ============================================================================
function GpsIndicator({ pos, error }) {
  const ok = pos && !error;
  return (
    <div className={`mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold ${ok ? "text-emerald-600" : "text-red-600"}`} data-testid="livreur-gps-status">
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
      {ok ? "GPS actif · visible pour l'auto-assign" : (error || "GPS en cours…")}
    </div>
  );
}


// ============================================================================
// Modal keypad PIN — validation livraison haute valeur (≥ 80 €)
// Grands boutons 20% de haut, feedback vibration, effacement facile.
// ============================================================================
function PinKeypadModal({ order, busy, onSubmit, onClose }) {
  const [pin, setPin] = useState("");
  const push = (d) => {
    if (pin.length >= 4) return;
    try { if ("vibrate" in navigator) navigator.vibrate(15); } catch { /* ignore */ }
    setPin((p) => (p + d).slice(0, 4));
  };
  const back = () => setPin((p) => p.slice(0, -1));
  const submit = () => {
    if (pin.length !== 4) { toast.error("Saisis les 4 chiffres."); return; }
    onSubmit(pin);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="pin-keypad-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F26419]/10 text-[#B85014] px-3 py-1 text-[10px] font-black uppercase tracking-widest">
              <KeyRound className="w-3 h-3" /> Livraison sécurisée
            </div>
            <h3 className="mt-2 font-head font-black text-2xl text-[#1A1A1A]">Code du client</h3>
            <p className="text-sm text-[#75807A] mt-1">Commande #{(order.id || "").slice(0, 8).toUpperCase()} · <b>{Number(order.total_eur || 0).toFixed(2)} €</b><br/>Demande les 4 chiffres au client.</p>
          </div>
          <button onClick={onClose} className="text-[#75807A] hover:text-[#1A1A1A] p-2 rounded-lg hover:bg-[#F0EBE1]" data-testid="pin-keypad-close"><X className="w-4 h-4" /></button>
        </div>
        {/* Cases PIN */}
        <div className="grid grid-cols-4 gap-3 my-5" data-testid="pin-keypad-display">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`h-16 rounded-2xl border-2 flex items-center justify-center text-4xl font-black tabular-nums ${pin[i] ? "bg-[#F26419] border-[#F26419] text-white" : "bg-[#FDF8F0] border-[#E2DFD8] text-[#75807A]"}`}>
              {pin[i] ? "●" : ""}
            </div>
          ))}
        </div>
        {/* Clavier 3 x 4 */}
        <div className="grid grid-cols-3 gap-2">
          {["1","2","3","4","5","6","7","8","9"].map((d) => (
            <button key={d} onClick={() => push(d)} data-testid={`pin-keypad-${d}`}
              className="h-16 rounded-2xl bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] hover:bg-[#FFF3E6] text-3xl font-black text-[#1A1A1A] transition-colors active:scale-95">
              {d}
            </button>
          ))}
          <button onClick={back} data-testid="pin-keypad-back"
            className="h-16 rounded-2xl bg-white border-2 border-[#E2DFD8] hover:border-[#DC2626] text-[#DC2626] font-bold text-sm active:scale-95">
            ← Effacer
          </button>
          <button onClick={() => push("0")} data-testid="pin-keypad-0"
            className="h-16 rounded-2xl bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] hover:bg-[#FFF3E6] text-3xl font-black text-[#1A1A1A] active:scale-95">
            0
          </button>
          <button onClick={submit} disabled={busy || pin.length !== 4} data-testid="pin-keypad-submit"
            className="h-16 rounded-2xl bg-[#16a34a] hover:bg-[#127a37] text-white font-black text-sm disabled:opacity-40 active:scale-95 inline-flex items-center justify-center gap-1">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5" /> OK</>}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Bandeau météo — motive le livreur selon la condition du jour (Open-Meteo).
// Aucune clé API requise, données pour Bruxelles par défaut.
// ============================================================================
function WeatherBanner({ weather }) {
  const w = weather || {};
  const code = Number(w.weathercode);
  const temp = Math.round(Number(w.temperature || 0));
  // Codes WMO : 0=clear, 1-3=partly cloudy, 45-48=fog, 51-67=rain, 71-77=snow, 80-82=showers, 95-99=storm
  let Icon = Sun, bg = "from-[#FDBA74] to-[#F97316]", tip = "Belle journée pour rider — sois vu, sois safe 🚲";
  if (code >= 71 && code <= 77) { Icon = Snowflake; bg = "from-[#93C5FD] to-[#3B82F6]"; tip = "Neige — gants + roule doucement, tu es notre héros ❄️"; }
  else if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) { Icon = CloudRain; bg = "from-[#60A5FA] to-[#2563EB]"; tip = "Pluie — coupe-vent + phares allumés, les pourboires suivent 💛"; }
  else if (code >= 1 && code <= 3) { Icon = Cloud; bg = "from-[#94A3B8] to-[#475569]"; tip = "Ciel voilé — temps parfait pour livrer sans transpirer 💪"; }
  else if (code >= 95) { Icon = CloudRain; bg = "from-[#7C3AED] to-[#4C1D95]"; tip = "Orage — mets-toi à l'abri si ça tape, on n'a rien à gagner sous la foudre ⚡"; }
  if (temp <= 5) tip = "Grand froid — plusieurs couches + pause chaude toutes les 2 h 🔥";
  return (
    <div className={`mb-4 rounded-2xl bg-gradient-to-br ${bg} text-white p-4 shadow-md flex items-center gap-3`} data-testid="livreur-weather">
      <div className="w-12 h-12 rounded-2xl bg-white/20 grid place-items-center shrink-0"><Icon className="w-6 h-6" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-head font-black text-2xl leading-none tabular-nums">{temp}°C</div>
        <div className="text-xs opacity-95 mt-1 leading-snug">{tip}</div>
      </div>
    </div>
  );
}


// ============================================================================
// Bloc stats accueil livreur : gains jour/semaine, livraisons, note, en-ligne
// Charge dynamiquement /api/driver/home-stats + poll toutes les 20 s.
// Toggle en-ligne via PUT /api/driver/availability avec position GPS courante.
// ============================================================================
function DriverHomeStats() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/home-stats");
      setStats(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);

  const toggleOnline = async () => {
    if (busy || !stats) return;
    setBusy(true);
    try {
      const wantOnline = !stats.online;
      let lat, lng;
      if (wantOnline && "geolocation" in navigator) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (p) => { lat = p.coords.latitude; lng = p.coords.longitude; resolve(); },
            () => resolve(),
            { timeout: 5000, maximumAge: 30000 }
          );
        });
      }
      await api.put("/driver/availability", { available: wantOnline, lat, lng });
      toast.success(wantOnline ? "Vous êtes en ligne ✅" : "Vous êtes hors ligne");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de changer votre statut.");
    } finally { setBusy(false); }
  };

  if (!stats) {
    return (
      <div className="mb-6 h-40 rounded-3xl bg-white border border-[#E2DFD8] animate-pulse" data-testid="driver-home-stats-loading" />
    );
  }
  const isEu = (stats.zone || "").toLowerCase() === "europe";
  const money = (eur, fcfa) => isEu
    ? `${(eur || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
    : `${(fcfa || 0).toLocaleString("fr-FR")} FCFA`;
  const t = stats.today || {}, w = stats.week || {};
  return (
    <div className="mb-6 space-y-3" data-testid="driver-home-stats">
      {/* Toggle en ligne / hors ligne */}
      <button onClick={toggleOnline} disabled={busy || !stats.kyc_approved}
        data-testid="driver-online-toggle"
        className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl transition-all shadow-md ${
          stats.online
            ? "bg-gradient-to-r from-[#059669] to-[#047857] text-white hover:from-[#047857]"
            : "bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] hover:border-[#059669]"
        } disabled:opacity-60`}>
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${stats.online ? "bg-white animate-pulse" : "bg-[#75807A]"}`} />
          <div className="text-left">
            <div className="font-head font-black text-lg leading-none">{stats.online ? "En ligne" : "Hors ligne"}</div>
            <div className={`text-[11px] mt-0.5 ${stats.online ? "opacity-90" : "text-[#75807A]"}`}>
              {stats.kyc_approved
                ? (stats.online ? "Vous recevez des courses" : "Passez en ligne pour recevoir")
                : "KYC en attente"}
            </div>
          </div>
        </div>
        <div className={`text-xs font-bold uppercase tracking-wider ${stats.online ? "opacity-90" : "text-[#059669]"}`}>
          {busy ? "…" : (stats.online ? "Toucher pour couper" : "Toucher pour activer")}
        </div>
      </button>

      {/* Gains jour / semaine */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-[#059669] to-[#047857] text-white p-4" data-testid="stats-today">
          <div className="text-[10px] uppercase tracking-widest font-black opacity-90 inline-flex items-center gap-1.5"><Wallet className="w-3 h-3" /> Aujourd'hui</div>
          <div className="mt-2 font-head font-black text-2xl leading-none tabular-nums" data-testid="stats-today-earnings">{money(t.eur_net, t.fcfa_net)}</div>
          <div className="mt-1 text-[11px] opacity-90">{t.deliveries || 0} livraison{(t.deliveries || 0) > 1 ? "s" : ""}</div>
        </div>
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-4" data-testid="stats-week">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#75807A] inline-flex items-center gap-1.5"><Wallet className="w-3 h-3" /> Cette semaine</div>
          <div className="mt-2 font-head font-black text-2xl leading-none text-[#1A1A1A] tabular-nums" data-testid="stats-week-earnings">{money(w.eur_net, w.fcfa_net)}</div>
          <div className="mt-1 text-[11px] text-[#75807A]">{w.deliveries || 0} livraison{(w.deliveries || 0) > 1 ? "s" : ""}</div>
        </div>
      </div>

      {/* Note moyenne + total livraisons */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-4 flex items-center gap-3" data-testid="stats-rating">
          <div className="w-11 h-11 rounded-full bg-[#FEF3C7] grid place-items-center text-2xl shrink-0">⭐</div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-black text-[#75807A]">Note</div>
            <div className="mt-0.5 font-head font-black text-xl text-[#1A1A1A] tabular-nums">
              {stats.rating_count > 0 ? Number(stats.rating).toFixed(2) : "—"}
              {stats.rating_count > 0 && <span className="ml-1 text-[11px] font-semibold text-[#75807A]">/ 5</span>}
            </div>
            <div className="text-[11px] text-[#75807A]">{stats.rating_count} avis</div>
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-4 flex items-center gap-3" data-testid="stats-total">
          <div className="w-11 h-11 rounded-full bg-[#D1FAE5] grid place-items-center text-2xl shrink-0">📦</div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-black text-[#75807A]">Total</div>
            <div className="mt-0.5 font-head font-black text-xl text-[#1A1A1A] tabular-nums">{stats.total_deliveries || 0}</div>
            <div className="text-[11px] text-[#75807A]">livraisons</div>
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-4 flex items-center gap-3" data-testid="stats-km">
          <div className="w-11 h-11 rounded-full bg-[#DBEAFE] grid place-items-center text-2xl shrink-0">🚴</div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-black text-[#75807A]">Distance</div>
            <div className="mt-0.5 font-head font-black text-xl text-[#1A1A1A] tabular-nums">{Number(t.km || 0).toFixed(1)}</div>
            <div className="text-[11px] text-[#75807A]">km aujourd'hui</div>
          </div>
        </div>
      </div>
    </div>
  );
}
